import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { homePath, type Permission } from '@codepass/shared';
import { useAuth } from '@/auth/AuthProvider';
import { Spinner } from '@/components/ui';

/**
 * Route guards. De er en UX-mekanisme, ikke sikkerhed: den rigtige haandhaevelse ligger i
 * API'ets middleware og i RLS. En bruger der omgaar disse, faar 403 fra serveren.
 */

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  /*
   * Her stod tidligere det tvungne password-skift fra PRD par. 2.1. Det er fjernet sammen
   * med selve skiftesiden: login gaar gennem Microsoft, og adgangskoden hoerer til
   * Microsoft-kontoen. Flowet var i oevrigt allerede dødt - loadProfile i API'et svarer
   * altid must_change_password: false, fordi kolonnen ikke findes i det skema appen koerer
   * mod.
   */
  return <>{children}</>;
}

/**
 * Siden kraever en rettighed - eller mindst én af flere, fx /fakturering/:id, som baade
 * fakturaudkastene og historikken aabner. Se PERMISSIONS i @codepass/shared.
 *
 * `allowed` er til det, der ikke er en rettighed - rapporterne, som vaelges pr. rolle. Er den
 * sat, afgoer den alene.
 *
 * `fallback="home"` sender brugeren til sin egen startside i stedet for /forbudt. Det er til
 * /dashboard, som login og fejlsiderne sender alle til: en saelger, der logger ind, skal lande
 * paa kontrakterne og ikke paa en side, der siger nej.
 */
export function RoleGuard({
  permission,
  allowed,
  fallback = 'forbidden',
  children,
}: {
  permission?: Permission | readonly Permission[];
  allowed?: boolean;
  fallback?: 'forbidden' | 'home';
  children: ReactNode;
}) {
  const { canAny, user } = useAuth();
  const permissions: readonly Permission[] =
    permission === undefined ? [] : typeof permission === 'string' ? [permission] : permission;
  const ok = allowed ?? canAny(...permissions);

  if (!ok) {
    return <Navigate to={fallback === 'home' ? homePath(user) : '/forbudt'} replace />;
  }
  return <>{children}</>;
}

/** "/" - den foerste side, brugerens rettigheder aabner. Se `homePath`. */
export function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={homePath(user)} replace />;
}
